
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const RuralGroceryStoreDevelopmentPage = () => {
  useEffect(() => {
    document.title = 'Rural Grocery Store Development - Research Report | TS Dotyeni Investments';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'The importance of developing modern grocery infrastructure to replace traditional spaza shops in rural South Africa. Comprehensive analysis of retail opportunities and market potential.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c496c6842cb9024a7aeff60abbce199d.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Rural Grocery Store Development</h1>
            <p className="text-xl leading-relaxed">
              Transforming rural retail through modern grocery infrastructure and community-centered commerce
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
            <p className="text-gray-700 leading-relaxed">
              Rural South Africa's retail landscape is dominated by informal spaza shops that, while culturally significant, often lack the infrastructure, product variety, and competitive pricing that modern consumers demand. This analysis explores the substantial opportunity to develop modern grocery store networks that can serve rural communities more effectively while creating sustainable business models and employment opportunities.
            </p>
          </div>
        </section>

        {/* Current Retail Landscape */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Rural Retail Landscape</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <img 
                src="https://readdy.ai/api/search-image?query=Traditional%20South%20African%20spaza%20shop%20in%20rural%20township%2C%20small%20informal%20retail%20store%20with%20basic%20goods%2C%20local%20community%20members%20shopping%2C%20typical%20informal%20retail%20environment&width=400&height=250&seq=spaza-shop&orientation=landscape"
                alt="Traditional spaza shop"
                className="w-full h-32 object-cover object-top rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Traditional Spaza Shops</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Limited product selection (200-500 items)
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Higher prices due to inefficient supply chains
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Inconsistent product availability
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Limited refrigeration and storage
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20rural%20grocery%20store%20interior%2C%20well-organized%20shelves%20with%20diverse%20products%2C%20refrigeration%20units%2C%20clean%20shopping%20environment%2C%20professional%20retail%20layout&width=400&height=250&seq=modern-grocery&orientation=landscape"
                alt="Modern grocery store"
                className="w-full h-32 object-cover object-top rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Modern Grocery Stores</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Comprehensive product range (2,000+ items)
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Competitive pricing through scale
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Reliable supply chain management
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                  Fresh produce and refrigerated goods
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Market Opportunity */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Market Opportunity Analysis</h2>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Rural%20South%20African%20market%20analysis%20visualization%2C%20demographic%20data%20charts%2C%20consumer%20spending%20patterns%2C%20retail%20market%20opportunity%20graphs%2C%20economic%20development%20indicators&width=800&height=300&seq=market-analysis&orientation=landscape"
              alt="Market opportunity analysis"
              className="w-full h-48 object-cover object-top rounded-lg mb-6"
            />
            
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">18M</div>
                <div className="text-gray-600 text-sm">Rural Population</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">R180B</div>
                <div className="text-gray-600 text-sm">Annual Spending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">120,000</div>
                <div className="text-gray-600 text-sm">Spaza Shops</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">25%</div>
                <div className="text-gray-600 text-sm">Price Premium</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Market Insights</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <i className="ri-arrow-right-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Rural consumers pay 15-30% more for basic goods compared to urban areas</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-arrow-right-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Limited access to fresh produce and refrigerated goods creates unmet demand</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-arrow-right-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Growing middle class in rural areas seeks improved shopping experiences</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-arrow-right-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Government support for rural economic development creates favorable environment</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Development Model */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Grocery Store Development Model</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Store Format Strategy</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-store-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Community Stores</h4>
                  <div className="text-sm text-gray-600 mb-2">500-1,000 sqm</div>
                  <p className="text-gray-600 text-sm">Full-service grocery stores serving 5,000-10,000 people with comprehensive product range and fresh produce.</p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shopping-bag-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Express Stores</h4>
                  <div className="text-sm text-gray-600 mb-2">200-400 sqm</div>
                  <p className="text-gray-600 text-sm">Convenience-focused stores in smaller communities, emphasizing essential goods and quick shopping.</p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-truck-line text-emerald-600 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Mobile Stores</h4>
                  <div className="text-sm text-gray-600 mb-2">Truck-based</div>
                  <p className="text-gray-600 text-sm">Mobile retail units serving remote areas on scheduled routes, bringing goods directly to customers.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Supply Chain Innovation</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20retail%20supply%20chain%20in%20rural%20Africa%2C%20distribution%20center%20with%20trucks%2C%20efficient%20logistics%20operations%2C%20technology-enabled%20inventory%20management%2C%20rural%20delivery%20systems&width=800&height=300&seq=supply-chain&orientation=landscape"
                  alt="Supply chain innovation"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Regional Distribution Centers</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Centralized procurement and inventory management
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Cold storage and fresh produce handling
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Efficient last-mile delivery networks
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Technology Integration</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Real-time inventory tracking and management
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Mobile payment and digital banking integration
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        Customer loyalty and data analytics programs
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Community Impact */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Community Impact & Benefits</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Economic Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-briefcase-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Direct employment: 15-25 jobs per store</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-money-dollar-circle-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Cost savings: 15-25% reduction in grocery costs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-plant-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Local sourcing: Support for regional farmers</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-line text-emerald-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Infrastructure: Improved commercial development</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-heart-line text-blue-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Improved nutrition through fresh produce access</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Time savings: Reduced travel to urban centers</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-graduation-cap-line text-blue-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Skills development and training programs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-community-line text-blue-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Community gathering spaces and services</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Financial Projections */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Financial Performance & Projections</h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">R2-5M</div>
              <div className="text-gray-600 text-sm">Investment per Store</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">18-24</div>
              <div className="text-gray-600 text-sm">Payback (Months)</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">15-20%</div>
              <div className="text-gray-600 text-sm">Net Margin</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-emerald-600 mb-2">25-35%</div>
              <div className="text-gray-600 text-sm">ROI</div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Model Breakdown</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-600 mb-2">70%</div>
                <div className="text-sm text-gray-600">Grocery Sales</div>
                <div className="text-xs text-gray-500">Core retail operations</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-600 mb-2">20%</div>
                <div className="text-sm text-gray-600">Fresh Produce</div>
                <div className="text-xs text-gray-500">Higher margin fresh goods</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-600 mb-2">10%</div>
                <div className="text-sm text-gray-600">Services</div>
                <div className="text-xs text-gray-500">Financial services, mobile top-ups</div>
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Strategy */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Implementation Strategy</h2>
          
          <div className="bg-emerald-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Phased Rollout Approach</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <span className="text-white font-semibold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pilot Phase (Months 1-12)</h4>
                  <p className="text-gray-700">Launch 3-5 stores in selected communities to test and refine the model, establish supply chains, and build local partnerships.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <span className="text-white font-semibold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Regional Expansion (Months 13-36)</h4>
                  <p className="text-gray-700">Scale to 15-20 stores across target regions, establish distribution centers, and implement technology systems.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <span className="text-white font-semibold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Network Development (Months 37-60)</h4>
                  <p className="text-gray-700">Expand to 50+ stores, introduce mobile units, and develop additional services like financial products and agricultural support.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Strategic Conclusion</h2>
          <div className="bg-gray-50 rounded-2xl p-8">
            <p className="text-gray-700 leading-relaxed mb-6">
              The development of modern grocery store networks in rural South Africa represents a compelling investment opportunity that addresses significant market gaps while creating substantial social impact. With proper execution, these projects can achieve attractive financial returns while improving quality of life for rural communities.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Success requires careful attention to community engagement, supply chain efficiency, and technology integration. The combination of unmet market demand, supportive government policies, and proven retail models creates a favorable environment for sustainable growth and positive transformation of rural commerce.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Invest in Rural Retail Transformation</h3>
            <p className="text-emerald-100 mb-6">
              Join us in revolutionizing rural commerce while building a profitable and sustainable business.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Explore Investment Opportunities
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RuralGroceryStoreDevelopmentPage;
